/**
 * ImageCropper — recorte de imagem estilo WhatsApp.
 * Proporção fixa 4:3 (480×360 na tela, exporta 800×600px JPEG).
 * Arraste para reposicionar · scroll para zoom.
 */
import { useState, useRef, useEffect, useCallback } from 'react';

const CROP_W  = 480;   // largura da área de corte em tela
const CROP_H  = 360;   // altura  (4:3)
const PAD     = 48;    // surround escuro ao redor
const OUT_W   = 800;   // largura do JPEG exportado
const OUT_H   = 600;   // altura  do JPEG exportado
const STAGE_W = CROP_W + PAD * 2;
const STAGE_H = CROP_H + PAD * 2;

/* Quatro marcas de canto em L */
function CornerMarks() {
  const corners = [
    { top: -2, left: -2,  borderTop: '3px solid #fff', borderLeft: '3px solid #fff'  },
    { top: -2, right: -2, borderTop: '3px solid #fff', borderRight: '3px solid #fff' },
    { bottom: -2, left: -2,  borderBottom: '3px solid #fff', borderLeft: '3px solid #fff'  },
    { bottom: -2, right: -2, borderBottom: '3px solid #fff', borderRight: '3px solid #fff' },
  ];
  return corners.map((s, i) => (
    <div key={i} style={{ position: 'absolute', width: 20, height: 20, ...s }} />
  ));
}

export default function ImageCropper({ src, onConfirm, onCancel }) {
  const imgRef   = useRef(null);
  const stageRef = useRef(null);
  const natRef   = useRef({ w: 0, h: 0 });   // dimensões naturais da imagem
  const dragRef  = useRef(null);              // dados do arraste em curso

  const [imgNat, setImgNat] = useState({ w: 0, h: 0 }); // dispara re-render ao carregar
  const [scale,  setScale]  = useState(1);
  const [pos,    setPos]    = useState({ x: PAD, y: PAD });
  const [ready,  setReady]  = useState(false);

  /* Garante que a imagem sempre cobre o frame de corte */
  const clamp = useCallback((nx, ny, sc) => {
    const { w, h } = natRef.current;
    if (!w || !h) return { x: nx, y: ny };
    const dw = w * sc;
    const dh = h * sc;
    const x = Math.min(PAD, Math.max(PAD + CROP_W - dw, nx));
    const y = Math.min(PAD, Math.max(PAD + CROP_H - dh, ny));
    return { x, y };
  }, []);

  /* Inicializa posição quando a imagem carrega */
  const onImgLoad = useCallback(() => {
    const { naturalWidth: w, naturalHeight: h } = imgRef.current;
    natRef.current = { w, h };
    setImgNat({ w, h });
    // Scale mínimo para cobrir o frame (cover)
    const minSc = Math.max(CROP_W / w, CROP_H / h);
    const dw = w * minSc;
    const dh = h * minSc;
    const x = PAD + (CROP_W - dw) / 2;
    const y = PAD + (CROP_H - dh) / 2;
    setScale(minSc);
    setPos({ x, y });
    setReady(true);
  }, []);

  /* Zoom com scroll (zoom centrado no cursor) */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const handler = (e) => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
      const { w, h } = natRef.current;
      if (!w) return;
      const minSc = Math.max(CROP_W / w, CROP_H / h);
      setScale(prev => {
        const next = Math.max(minSc, Math.min(prev * factor, minSc * 6));
        setPos(prevPos => {
          // mantém o pixel da imagem sob o cursor no mesmo lugar
          const imgX = (cx - prevPos.x) / prev;
          const imgY = (cy - prevPos.y) / prev;
          return clamp(cx - imgX * next, cy - imgY * next, next);
        });
        return next;
      });
    };
    stage.addEventListener('wheel', handler, { passive: false });
    return () => stage.removeEventListener('wheel', handler);
  }, [clamp]);

  /* Arraste */
  const onPointerDown = (e) => {
    e.preventDefault();
    stageRef.current.setPointerCapture(e.pointerId);
    dragRef.current = { sx: e.clientX, sy: e.clientY, p0: { ...pos } };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const { sx, sy, p0 } = dragRef.current;
    setPos(clamp(p0.x + (e.clientX - sx), p0.y + (e.clientY - sy), scale));
  };
  const onPointerUp = () => { dragRef.current = null; };

  /* Exporta o recorte via Canvas */
  const confirm = () => {
    const srcX = (PAD - pos.x) / scale;
    const srcY = (PAD - pos.y) / scale;
    const srcW = CROP_W / scale;
    const srcH = CROP_H / scale;
    const canvas = document.createElement('canvas');
    canvas.width  = OUT_W;
    canvas.height = OUT_H;
    canvas.getContext('2d').drawImage(imgRef.current, srcX, srcY, srcW, srcH, 0, 0, OUT_W, OUT_H);
    canvas.toBlob(blob => onConfirm(blob), 'image/jpeg', 0.92);
  };

  const dispW = imgNat.w * scale;
  const dispH = imgNat.h * scale;

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.75)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: STAGE_W + 48 }}>
        <div className="modal-content" style={{ background: '#111', border: 'none' }}>

          {/* Header */}
          <div className="modal-header" style={{ borderColor: '#2a2a2a' }}>
            <div>
              <h5 className="modal-title text-white mb-0">Ajustar imagem</h5>
              <small style={{ color: '#888', fontSize: 12 }}>
                Arraste para reposicionar &nbsp;·&nbsp; Scroll para zoom &nbsp;·&nbsp; Proporção 4:3
              </small>
            </div>
            <button className="btn-close btn-close-white" onClick={onCancel} />
          </div>

          {/* Stage */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 24px 12px' }}>
            <div
              ref={stageRef}
              style={{
                width: STAGE_W, height: STAGE_H,
                background: '#000',
                position: 'relative',
                overflow: 'hidden',
                cursor: ready ? 'grab' : 'default',
                userSelect: 'none',
                borderRadius: 4,
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              {/* Imagem */}
              <img
                ref={imgRef}
                src={src}
                onLoad={onImgLoad}
                alt="recorte"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: pos.x, top: pos.y,
                  width: dispW || 'auto',
                  height: dispH || 'auto',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  display: ready ? 'block' : 'none',
                  imageRendering: 'high-quality',
                }}
              />

              {/* Spinner enquanto carrega */}
              {!ready && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="spinner-border text-secondary" />
                </div>
              )}

              {ready && <>
                {/* Surround escuro: 4 tiras ao redor do frame */}
                <div style={{ position: 'absolute', inset: 0, top: 0, height: PAD, background: 'rgba(0,0,0,.62)', zIndex: 10, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: PAD, background: 'rgba(0,0,0,.62)', zIndex: 10, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: PAD, bottom: PAD, left: 0, width: PAD, background: 'rgba(0,0,0,.62)', zIndex: 10, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: PAD, bottom: PAD, right: 0, width: PAD, background: 'rgba(0,0,0,.62)', zIndex: 10, pointerEvents: 'none' }} />

                {/* Frame de recorte */}
                <div style={{
                  position: 'absolute',
                  top: PAD, left: PAD,
                  width: CROP_W, height: CROP_H,
                  border: '1.5px solid rgba(255,255,255,.75)',
                  zIndex: 11, pointerEvents: 'none',
                }}>
                  {/* Linhas de terços */}
                  <div style={{ position: 'absolute', inset: 0, left: '33.33%', right: 'auto', width: 1, background: 'rgba(255,255,255,.18)' }} />
                  <div style={{ position: 'absolute', inset: 0, left: '66.66%', right: 'auto', width: 1, background: 'rgba(255,255,255,.18)' }} />
                  <div style={{ position: 'absolute', inset: 0, top: '33.33%', bottom: 'auto', height: 1, background: 'rgba(255,255,255,.18)' }} />
                  <div style={{ position: 'absolute', inset: 0, top: '66.66%', bottom: 'auto', height: 1, background: 'rgba(255,255,255,.18)' }} />
                  {/* Marcas de canto */}
                  <CornerMarks />
                </div>

                {/* Label de proporção */}
                <div style={{
                  position: 'absolute', top: PAD + 8, left: PAD + 8,
                  fontSize: 10, color: 'rgba(255,255,255,.5)',
                  fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
                  zIndex: 12, pointerEvents: 'none',
                }}>4:3</div>
              </>}
            </div>
          </div>

          {/* Dica de zoom */}
          {ready && (
            <div style={{ textAlign: 'center', color: '#555', fontSize: 11, marginBottom: 4 }}>
              Use o scroll do mouse para ampliar ou reduzir
            </div>
          )}

          {/* Footer */}
          <div className="modal-footer" style={{ borderColor: '#2a2a2a', background: '#111' }}>
            <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={confirm} disabled={!ready}>
              Usar este recorte
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
